import React, { FC } from 'react';
import { Redirect, useRouteMatch } from 'react-router';

import { FeedKey, GetGroupComponent, Group as GroupType } from 'api';
import { Layouts, Loaders, QueryManager } from 'components';
import { FeedSelect } from 'features/feed';
import { useCurrentUser, useFollowed, useLocalStorage } from 'hooks';
import { DEFAULT_SORT } from 'constants/index';
import { isAdminUser } from 'helpers';
import * as paths from 'paths';

import { IMatchParams } from '../types';
import { canModerateGroupContent } from '../helpers';

import GroupMembers from '../components/GroupMembers';
import GroupPosts from '../components/GroupPosts/GroupPostsIndex';
import GroupScheduled from '../components/GroupScheduled';
import GroupApproval from '../components/GroupApproval';
import GroupRemoved from '../components/GroupRemoved';
import { FeedMenu } from '../components/Group';

const Group: FC = () => {
  const currentUser = useCurrentUser();
  const { groups } = useFollowed();
  const match = useRouteMatch<IMatchParams>();
  const [sort, setSort] = useLocalStorage('timelineSort', DEFAULT_SORT);

  const isPosts = match.path === paths.group;
  const isMembers = match.path === paths.groupMembers;
  const isApproval = match.path === paths.groupApproval;
  const isScheduled = match.path === paths.groupScheduled;
  const isRemoved = match.path === paths.groupRemoved;

  const { group_id } = match.params;
  const isFollowing = groups.some(group => group.id === group_id);

  function handleSort() {
    setSort(!sort);
  }

  function renderMenu() {
    const selectedGroup = groups.find(group => group.id === group_id);

    if (!selectedGroup) {
      return null;
    }

    return <FeedMenu enableSort selectedGroup={selectedGroup} onSort={handleSort} sort={sort} />;
  }

  function canModerate(group: GroupType) {
    return canModerateGroupContent(currentUser, group.id);
  }

  function renderGroup(group: GroupType) {
    const variables = {
      sort,
      key: FeedKey.Group,
      id: group.id,
      limit: 10,
    };

    if (group.is_private && !isAdminUser(currentUser) && !isFollowing) {
      return <Redirect to={paths.groups} />;
    }

    if (isPosts) {
      return <GroupPosts variables={variables} isFollowing={isFollowing} group={group} />;
    }

    if (isMembers) {
      return <GroupMembers isFollowing={isFollowing} group={group} />;
    }

    if (canModerate(group) && isApproval) {
      variables.key = FeedKey.Submittedgroup;
      return <GroupApproval variables={variables} group={group} />;
    }

    if (canModerate(group) && isScheduled) {
      variables.key = 'scheduled' as FeedKey;
      return <GroupScheduled variables={variables} group={group} />;
    }

    if (canModerate(group) && isRemoved) {
      variables.key = FeedKey.Removedgroup;
      return <GroupRemoved variables={variables} group={group} />;
    }

    return <Redirect to={paths.groups} />;
  }

  function renderLoader() {
    return (
      <Layouts.AppMain2Column
        hideRightOnMobile
        desktopSplit="8/4"
        left={<Loaders.Widget />}
        right={<Loaders.Widget />}
      />
    );
  }

  return (
    <Layouts.App2Column
      sidebar={<FeedSelect menu={renderMenu()} />}
      main={
        <GetGroupComponent variables={{ id: group_id }}>
          {queryResult => (
            <QueryManager loader={renderLoader()} {...queryResult}>
              {({ data }) => {
                if (!data || !data.getGroup) {
                  return <Redirect to={paths.groups} />;
                }

                const { getGroup } = data;
                return renderGroup(getGroup);
              }}
            </QueryManager>
          )}
        </GetGroupComponent>
      }
    />
  );
};

export default Group;
